import { buttonVariants } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DayPicker } from 'react-day-picker';
import { format } from 'date-fns';
import * as React from 'react';
import { ru } from 'date-fns/locale';

const WEEKDAY_NAMES = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

const capitalize = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

function Calendar({ className, classNames, formatters, showOutsideDays = true, ...props }: CalendarProps) {
  return (
    <DayPicker
      className={cn('p-3', className)}
      classNames={{
        caption: 'relative flex items-center justify-center pt-1',
        caption_label: 'px-10 text-sm font-medium',
        cell: 'h-9 w-9 text-center text-sm p-0 relative focus-within:relative focus-within:z-20',
        day: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-9 w-9 p-0 font-normal aria-selected:opacity-100'
        ),
        day_disabled: 'text-muted-foreground opacity-50',
        day_hidden: 'invisible',
        day_outside:
          'day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30',
        day_range_end: 'day-range-end',
        day_range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground',
        day_selected:
          '!rounded-full !bg-primary !text-primary-foreground hover:!bg-primary hover:!text-primary-foreground focus:!bg-primary focus:!text-primary-foreground',
        day_today: 'bg-accent text-accent-foreground',
        head_cell: 'text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]',
        head_row: 'flex',
        month: 'space-y-4',
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
        nav: 'absolute inset-x-1 top-1 flex items-center justify-between',
        nav_button: cn(
          buttonVariants({ variant: 'outline' }),
          'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100'
        ),
        nav_button_next: '',
        nav_button_previous: '',
        row: 'flex w-full mt-2',
        table: 'w-full border-collapse space-y-1',
        ...classNames
      }}
      components={{
        IconLeft: ({ ..._props }) => <ChevronLeft className="h-4 w-4" />,
        IconRight: ({ ..._props }) => <ChevronRight className="h-4 w-4" />
      }}
      formatters={{
        formatCaption: (date) => capitalize(format(date, 'LLLL yyyy', { locale: ru })),
        formatWeekdayName: (date) => WEEKDAY_NAMES[date.getDay()],
        ...formatters
      }}
      locale={ru}
      showOutsideDays={showOutsideDays}
      weekStartsOn={1}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };